import { ChangeDetectionStrategy, Component, inject, OnDestroy } from "@angular/core";
import { Store } from "@ngxs/store";
import * as TagsActions from "@vp/data-access/tags";
import { SharedConfirmationService } from "@vp/shared/confirmation";
import { filterNullMap } from "@vp/shared/operators";
import * as TagManagerActions from "@vp/tag-manager/data-access/tag-manager-state";
import { TagManagerState } from "@vp/tag-manager/data-access/tag-manager-state";
import { Operation } from "rfc6902";
import { BehaviorSubject, Observable, of, Subject } from "rxjs";
import { map, switchMap, take } from "rxjs/operators";

@Component({
  selector: "vp-tag-manager-action-row",
  standalone: false,
  templateUrl: "./tag-manager-action-row.component.html",
  styleUrls: ["./tag-manager-action-row.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class TagManagerActionRowComponent implements OnDestroy {
  pendingOperations$: Observable<Operation[]> = inject(Store).select(TagManagerState.operations);
  uiState$ = inject(Store).select(TagManagerState.getUiState) as Observable<
    (filter: string) => boolean | string
  >;

  private readonly destroyed$ = new Subject<void>();
  private readonly successMessage$$ = new BehaviorSubject<string | null>(null);

  constructor(
    private readonly store: Store,
    private readonly confirmationDialog: SharedConfirmationService
  ) {}
  readonly successMessage$ = this.successMessage$$.pipe(filterNullMap());
  readonly changes$ = this.pendingOperations$.pipe(map(o => o.length));

  editStatus$ = this.uiState$.pipe(map(filterFn => filterFn("editStatus")));

  ngOnDestroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  save = () => {
    this.store.dispatch(new TagManagerActions.CommitOperations());
  };

  cancel = () => {
    this.pendingOperations$
      .pipe(
        take(1),
        switchMap(operations => {
          if (operations.length > 0) {
            return this.confirmationDialog
              .open(
                "You have unsaved changes. They will be lost if you continue.",
                "Continue",
                "Pending Changes",
                "tagManagerPendingChanges",
                "30vw",
                true,
                true,
                "warn"
              )
              .afterClosed();
          }
          return of(true);
        })
      )
      .subscribe(isCancelled => {
        if (isCancelled) {
          this.store.dispatch(new TagsActions.LoadTags(false));
          this.store.dispatch(new TagManagerActions.CancelEditingTag());
        }
      });
  };

  showPreview = () => {
    this.store.dispatch(
      new TagManagerActions.PatchUiState({
        showPreview: true
      })
    );
  };
}
